import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { getSecurity, saveSecurityQuestions, enableSecurity, removeTrustedIp, updateDefaultText } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { useMailStore } from '@/stores/mail';
import { listMailboxes, deleteMailbox, updateMailbox, changeMailboxPassword } from '@/api/mailboxes';
import { listFilters, createFilter, deleteFilter } from '@/api/filters';
import type { FilterRule } from '@/types';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, Trash2, LogOut, Plus, Filter, KeyRound, ChevronDown, RefreshCw } from 'lucide-react';
import { pushSubscribe, pushUnsubscribe, pushTest } from '@/api/push';

const FIELD_LABELS: Record<string, string> = {
  sender: 'Sender',
  subject: 'Subject',
  body: 'Body text',
  any: 'Anywhere'
};

function describeFilter(f: FilterRule): string {
  if (f.matchType === 'keyword') return `${FIELD_LABELS[f.field || 'any']} contains “${f.matchValue}”`;
  if (f.matchType === 'domain') return `Domain ${f.matchValue}`;
  if (f.matchType === 'email') return `Sender ${f.matchValue}`;
  return `Pattern ${f.matchValue}`;
}

declare const __APP_VERSION__: string;

export function SettingsRoute() {
  // Backend version/commit, so a stale cached frontend against a newer server is
  // visible rather than mysterious.
  const [build, setBuild] = useState<{ version?: string; commit?: string } | null>(null);
  useEffect(() => {
    fetch('/api/health').then((r) => r.json()).then(setBuild).catch(() => {});
  }, []);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme, loadImagesByDefault, setLoadImagesByDefault } = useUIStore();
  const { mailboxes, refreshMailboxes } = useMailStore();
  const setUser = useAuthStore((st) => st.setUser);
  const [defaultFont, setDefaultFont] = useState<string | null>(user?.defaultFont ?? null);
  const [pushOn, setPushOn] = useState(false);
  useEffect(() => {
    navigator.serviceWorker?.ready
      .then((r) => r.pushManager.getSubscription())
      .then((s) => setPushOn(!!s))
      .catch(() => {});
  }, []);
  const [defaultFontSize, setDefaultFontSize] = useState<number | null>(user?.defaultFontSize ?? null);
  const saveDefaultText = useMutation({
    mutationFn: () => updateDefaultText(defaultFont, defaultFontSize),
    onSuccess: () => {
      setUser({ ...(user as any), defaultFont, defaultFontSize });
    }
  });

  useQuery({ queryKey: ['mailboxes'], queryFn: listMailboxes });
  const { data: filtersData } = useQuery({ queryKey: ['filters'], queryFn: listFilters });
  const filters = filtersData?.filters || [];

  const [kw, setKw] = useState('');
  const [field, setField] = useState<'sender' | 'subject' | 'body' | 'any'>('subject');
  const [spamOpen, setSpamOpen] = useState(false);

  const addFilter = useMutation({
    mutationFn: () => createFilter({ listType: 'block', matchType: 'keyword', field, matchValue: kw.trim() }),
    onSuccess: () => {
      setKw('');
      queryClient.invalidateQueries({ queryKey: ['filters'] });
    }
  });
  const removeFilter = useMutation({
    mutationFn: (id: string) => deleteFilter(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['filters'] })
  });

  const [footerDrafts, setFooterDrafts] = useState<Record<string, string>>({});
  const saveFooter = useMutation({
    mutationFn: (p: { id: string; signature: string }) => updateMailbox(p.id, { signature: p.signature }),
    onSuccess: () => { refreshMailboxes(); queryClient.invalidateQueries({ queryKey: ['mailboxes'] }); }
  });
  const setCategory = useMutation({
    mutationFn: (p: { id: string; category: 'personal' | 'business' }) => updateMailbox(p.id, { category: p.category }),
    onSuccess: () => { refreshMailboxes(); queryClient.invalidateQueries({ queryKey: ['mailboxes'] }); }
  });
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const saveName = useMutation({
    mutationFn: (p: { id: string; displayName: string }) => updateMailbox(p.id, { displayName: p.displayName }),
    onSuccess: () => { refreshMailboxes(); queryClient.invalidateQueries({ queryKey: ['mailboxes'] }); }
  });

  // Change the real mailbox password (local/on-server mailboxes only).
  const [pwOpen, setPwOpen] = useState<string | null>(null);
  const [pwDraft, setPwDraft] = useState<{ p1: string; p2: string }>({ p1: '', p2: '' });
  const [pwMsg, setPwMsg] = useState<{ id: string; kind: 'ok' | 'err'; text: string } | null>(null);
  const changePwd = useMutation({
    mutationFn: (p: { id: string; newPassword: string }) => changeMailboxPassword(p.id, p.newPassword),
    onSuccess: (res, vars) => {
      setPwMsg({ id: vars.id, kind: 'ok', text: res.verified ? 'Password changed and verified.' : 'Password changed (could not re-verify login).' });
      setPwDraft({ p1: '', p2: '' });
      setPwOpen(null);
    },
    onError: (err: any, vars) => {
      setPwMsg({ id: vars.id, kind: 'err', text: err?.message || 'Could not change the password.' });
    }
  });

  // Account security: security questions + trusted-IP challenge.
  const { data: security } = useQuery({ queryKey: ['security'], queryFn: getSecurity });
  const [qDrafts, setQDrafts] = useState<{ question: string; answer: string }[]>([
    { question: '', answer: '' }, { question: '', answer: '' }, { question: '', answer: '' }
  ]);
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (security && !seeded) {
      if (security.questions.length === 3) {
        setQDrafts(security.questions.map((q) => ({ question: q.question, answer: '' })));
      }
      setSeeded(true);
    }
  }, [security, seeded]);
  const [secMsg, setSecMsg] = useState<string | null>(null);
  const saveQ = useMutation({
    mutationFn: () => saveSecurityQuestions(qDrafts),
    onSuccess: () => { setSecMsg('Security questions saved.'); queryClient.invalidateQueries({ queryKey: ['security'] }); },
    onError: (e: any) => setSecMsg(e?.message || 'Could not save questions.')
  });
  const toggleSec = useMutation({
    mutationFn: (enabled: boolean) => enableSecurity(enabled),
    onSuccess: () => { setSecMsg(null); queryClient.invalidateQueries({ queryKey: ['security'] }); },
    onError: (e: any) => setSecMsg(e?.message || 'Could not update.')
  });
  const removeIp = useMutation({
    mutationFn: (id: string) => removeTrustedIp(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['security'] })
  });

  const goToAddFilter = () => {
    setSpamOpen(true);
    document.getElementById('spam-filters')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => document.getElementById('filter-keyword')?.focus(), 300);
  };

  const inputCls =
    'rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold';

  return (
    <div className="flex h-full flex-col overflow-y-auto px-4 py-6 md:px-6">
      <h1 className="mb-6 text-2xl font-bold text-bmail-text">Settings</h1>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Account</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-medium text-bmail-text">{user?.displayName || user?.handle}</div>
              <div className="text-sm text-bmail-text-secondary">{user?.email || `@${user?.handle}`}</div>
            </div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-500/30 px-3 py-2 text-sm text-bmail-error hover:bg-bmail-error/10"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Appearance</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <button onClick={toggleTheme} className="flex w-full items-center justify-between text-left">
            <span className="text-bmail-text">Theme</span>
            <span className="flex items-center gap-2 text-bmail-text">
              {theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
              {theme === 'dark' ? 'Dark' : 'Light'}
            </span>
          </button>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Email</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <button
            onClick={() => setLoadImagesByDefault(!loadImagesByDefault)}
            className="flex w-full items-center justify-between text-left"
          >
            <span className="text-bmail-text">Download images by default</span>
            <span className={`flex h-6 w-11 shrink-0 items-center rounded-full px-0.5 transition-colors ${loadImagesByDefault ? 'bg-bmail-goldfill' : 'bg-bmail-border'}`}>
              <span className={`h-5 w-5 rounded-full bg-white transition-transform ${loadImagesByDefault ? 'translate-x-5' : 'translate-x-0'}`} />
            </span>
          </button>
          <p className="mt-2 text-xs text-bmail-text-secondary">
            When off, remote images stay hidden until you click “Download images” inside an email.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Account security</h2>
        <div className="space-y-4 rounded-xl border border-bmail-border bg-bmail-surface p-4">
          {/* Challenge toggle */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-bmail-text">Challenge logins from new locations</div>
              <p className="mt-1 text-xs text-bmail-text-secondary">
                When signing in from an unrecognized IP address, you’ll be asked 2 of your 3 security questions.
              </p>
            </div>
            <button
              onClick={() => toggleSec.mutate(!security?.enabled)}
              disabled={toggleSec.isPending || (!security?.enabled && !security?.hasQuestions)}
              className={`mt-0.5 flex h-6 w-11 shrink-0 items-center rounded-full px-0.5 transition-colors disabled:opacity-40 ${security?.enabled ? 'bg-bmail-goldfill' : 'bg-bmail-border'}`}
              aria-label="Toggle new-location challenge"
            >
              <span className={`h-5 w-5 rounded-full bg-white transition-transform ${security?.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          {!security?.hasQuestions && (
            <p className="text-xs text-bmail-text-secondary">Set your 3 security questions below to turn this on.</p>
          )}

          {/* Questions editor */}
          <div className="space-y-3 border-t border-bmail-border pt-3">
            <div className="flex items-center gap-2 text-sm font-medium text-bmail-text">
              Security questions
              {security?.hasQuestions && <span className="text-xs font-normal text-bmail-success">✓ set</span>}
            </div>
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1">
                <input
                  value={qDrafts[i].question}
                  onChange={(e) => setQDrafts((d) => d.map((q, idx) => (idx === i ? { ...q, question: e.target.value } : q)))}
                  placeholder={`Question ${i + 1}`}
                  className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-3 py-2 text-sm text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                />
                <input
                  value={qDrafts[i].answer}
                  onChange={(e) => setQDrafts((d) => d.map((q, idx) => (idx === i ? { ...q, answer: e.target.value } : q)))}
                  placeholder={security?.hasQuestions ? 'New answer' : 'Answer'}
                  autoComplete="off"
                  className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-3 py-2 text-sm text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                />
              </div>
            ))}
            <p className="text-xs text-bmail-text-secondary">Answers aren’t case-sensitive. Saving replaces all three.</p>
            <button
              onClick={() => { setSecMsg(null); saveQ.mutate(); }}
              disabled={saveQ.isPending || qDrafts.some((q) => !q.question.trim() || !q.answer.trim())}
              className="rounded-lg bg-bmail-goldfill px-3 py-1.5 text-xs font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
            >
              {saveQ.isPending ? 'Saving…' : 'Save questions'}
            </button>
            {secMsg && <p className="text-xs text-bmail-text-secondary">{secMsg}</p>}
          </div>

          {/* Trusted IPs */}
          <div className="border-t border-bmail-border pt-3">
            <div className="mb-2 text-sm font-medium text-bmail-text">Recognized locations</div>
            {security?.trustedIps?.length ? (
              <div className="space-y-1">
                {security.trustedIps.map((ip) => (
                  <div key={ip.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-bmail-text">{ip.ip}</span>
                    <button
                      onClick={() => removeIp.mutate(ip.id)}
                      className="shrink-0 text-xs font-medium text-bmail-error hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-bmail-text-secondary">No recognized locations yet — the ones you sign in from will appear here.</p>
            )}
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Mailboxes</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface">
          {mailboxes.map((m) => (
            <div key={m.id} className="border-b border-bmail-border last:border-0">
             <div className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0 flex-1">
                <input
                  value={nameDrafts[m.id] ?? m.displayName ?? ''}
                  onChange={(e) => setNameDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                  placeholder={m.email}
                  aria-label="Mailbox name"
                  className="w-full truncate border-b border-transparent bg-transparent font-medium text-bmail-text focus:border-bmail-gold focus:outline-none"
                />
                <div className="truncate text-xs text-bmail-text-secondary">{m.email}</div>
                {m.authFailed && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs font-medium text-bmail-error">Google connection expired —</span>
                    <button
                      onClick={() => { window.location.href = `${import.meta.env.VITE_API_BASE_URL || '/api'}/oauth/gmail/reauth/${m.id}`; }}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-bmail-goldfill hover:underline"
                    >
                      <RefreshCw size={12} /> Reconnect
                    </button>
                  </div>
                )}
              </div>
              {nameDrafts[m.id] !== undefined && nameDrafts[m.id] !== (m.displayName ?? '') && (
                <button
                  onClick={() => saveName.mutate({ id: m.id, displayName: nameDrafts[m.id] })}
                  disabled={saveName.isPending}
                  className="ml-2 shrink-0 rounded-lg bg-bmail-goldfill px-3 py-1.5 text-xs font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
                >
                  Save
                </button>
              )}
              <div className="ml-2 flex shrink-0 overflow-hidden rounded-lg border border-bmail-border text-xs">
                {(['personal', 'business'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory.mutate({ id: m.id, category: c })}
                    disabled={setCategory.isPending || m.category === c}
                    className={`px-2 py-1 capitalize transition-colors ${m.category === c ? 'bg-bmail-goldfill text-bmail-on-accent' : 'text-bmail-text-secondary hover:bg-bmail-surface-hover'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              {m.canChangePassword && (
                <button
                  onClick={() => { setPwMsg(null); setPwDraft({ p1: '', p2: '' }); setPwOpen(pwOpen === m.id ? null : m.id); }}
                  className={`ml-2 shrink-0 rounded-lg p-2 hover:bg-bmail-surface-hover ${pwOpen === m.id ? 'text-bmail-gold' : 'text-bmail-text-secondary hover:text-bmail-text'}`}
                  aria-label="Change password"
                  title="Change password"
                >
                  <KeyRound size={16} />
                </button>
              )}
              <button
                onClick={async () => { await deleteMailbox(m.id); refreshMailboxes(); }}
                className="ml-2 shrink-0 rounded-lg p-2 text-bmail-text-secondary hover:bg-bmail-error/10 hover:text-bmail-error"
                aria-label="Delete mailbox"
              >
                <Trash2 size={16} />
              </button>
             </div>

             {pwMsg?.id === m.id && (
               <div className={`mx-4 mb-3 rounded-lg px-3 py-2 text-xs ${pwMsg.kind === 'ok' ? 'bg-bmail-success/10 text-bmail-success' : 'bg-bmail-error/10 text-bmail-error'}`}>
                 {pwMsg.text}
               </div>
             )}

             {pwOpen === m.id && (
               <form
                 onSubmit={(e) => {
                   e.preventDefault();
                   if (pwDraft.p1 !== pwDraft.p2) { setPwMsg({ id: m.id, kind: 'err', text: 'Passwords do not match.' }); return; }
                   if (pwDraft.p1.length < 8) { setPwMsg({ id: m.id, kind: 'err', text: 'Use at least 8 characters.' }); return; }
                   setPwMsg(null);
                   changePwd.mutate({ id: m.id, newPassword: pwDraft.p1 });
                 }}
                 className="mx-4 mb-3 space-y-2 rounded-lg border border-bmail-border bg-bmail-navy p-3"
               >
                 <div className="text-xs text-bmail-text-secondary">
                   Sets a new password for <span className="font-medium text-bmail-text">{m.email}</span> on the mail server. It’ll also work for webmail. b-mail keeps using it automatically.
                 </div>
                 <input
                   type="password"
                   autoComplete="new-password"
                   placeholder="New password"
                   value={pwDraft.p1}
                   onChange={(e) => setPwDraft((d) => ({ ...d, p1: e.target.value }))}
                   className="w-full rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:border-bmail-gold focus:outline-none"
                 />
                 <input
                   type="password"
                   autoComplete="new-password"
                   placeholder="Confirm new password"
                   value={pwDraft.p2}
                   onChange={(e) => setPwDraft((d) => ({ ...d, p2: e.target.value }))}
                   className="w-full rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:border-bmail-gold focus:outline-none"
                 />
                 <div className="flex gap-2">
                   <button
                     type="submit"
                     disabled={changePwd.isPending || !pwDraft.p1}
                     className="rounded-lg bg-bmail-goldfill px-3 py-1.5 text-xs font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
                   >
                     {changePwd.isPending ? 'Changing…' : 'Change password'}
                   </button>
                   <button
                     type="button"
                     onClick={() => { setPwOpen(null); setPwDraft({ p1: '', p2: '' }); }}
                     className="rounded-lg border border-bmail-border px-3 py-1.5 text-xs font-medium text-bmail-text hover:bg-bmail-surface-hover"
                   >
                     Cancel
                   </button>
                 </div>
               </form>
             )}
            </div>
          ))}
          <button
            onClick={() => navigate('/onboarding')}
            className="flex w-full items-center justify-center gap-2 border-t border-bmail-border px-4 py-3 text-sm font-medium text-bmail-gold hover:bg-bmail-surface-hover"
          >
            <Plus size={16} /> Add mailbox
          </button>
          <button
            onClick={goToAddFilter}
            className="flex w-full items-center justify-center gap-2 border-t border-bmail-border px-4 py-3 text-sm font-medium text-bmail-gold hover:bg-bmail-surface-hover"
          >
            <Filter size={16} /> Add spam filter
          </button>
        </div>
      </section>

      <section id="spam-filters" className="mb-8">
        <button
          onClick={() => setSpamOpen((o) => !o)}
          className="mb-3 flex w-full items-center justify-between text-left text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary"
        >
          <span>Spam filters{filters.length > 0 ? ` (${filters.length})` : ''}</span>
          <ChevronDown size={16} className={`transition-transform ${spamOpen ? 'rotate-180' : ''}`} />
        </button>
        {spamOpen && (
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <label className="mb-2 block text-sm font-medium text-bmail-text">Send to Spam when…</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select value={field} onChange={(e) => setField(e.target.value as typeof field)} className={inputCls}>
              <option value="sender">Sender</option>
              <option value="subject">Subject</option>
              <option value="body">Body text</option>
              <option value="any">Anywhere</option>
            </select>
            <input
              id="filter-keyword"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && kw.trim()) addFilter.mutate(); }}
              placeholder="contains this keyword…"
              className={`flex-1 ${inputCls}`}
            />
            <button
              disabled={!kw.trim() || addFilter.isPending}
              onClick={() => addFilter.mutate()}
              className="rounded-lg bg-bmail-goldfill px-4 py-2 text-sm font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
            >
              Add filter
            </button>
          </div>
          {addFilter.isError && <p className="mt-2 text-xs text-bmail-error">Could not add that filter.</p>}

          <div className="mt-4 space-y-2">
            {filters.length === 0 && <div className="text-sm text-bmail-text-secondary">No filters yet.</div>}
            {filters.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-2 rounded-lg border border-bmail-border px-3 py-2">
                <span className="flex min-w-0 flex-1 items-center gap-2 text-sm text-bmail-text">
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${f.listType === 'block' ? 'bg-bmail-error/15 text-bmail-error' : 'bg-bmail-success/15 text-bmail-success'}`}>
                    {f.listType === 'block' ? 'Spam' : 'Allow'}
                  </span>
                  <span className="min-w-0 truncate">{describeFilter(f)}</span>
                </span>
                <button
                  onClick={() => removeFilter.mutate(f.id)}
                  className="shrink-0 rounded-lg p-2 text-bmail-text-secondary hover:bg-bmail-error/10 hover:text-bmail-error"
                  aria-label="Delete filter"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-bmail-text-secondary">
            Filters apply to newly-synced mail. “Allow” rules (e.g. your own domain) win over spam scoring.
          </p>
        </div>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Footers</h2>
        <div className="space-y-3">
          {mailboxes.length === 0 && (
            <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4 text-sm text-bmail-text-secondary">Add a mailbox to set a footer.</div>
          )}
          {mailboxes.map((m) => {
            const draft = footerDrafts[m.id] ?? m.signature ?? '';
            const dirty = draft !== (m.signature ?? '');
            return (
              <div key={m.id} className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm font-medium text-bmail-text">{m.displayName || m.email}</span>
                  <button
                    disabled={!dirty || saveFooter.isPending}
                    onClick={() => saveFooter.mutate({ id: m.id, signature: draft })}
                    className="shrink-0 rounded-lg bg-bmail-goldfill px-3 py-1.5 text-sm font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
                <textarea
                  value={draft}
                  onChange={(e) => setFooterDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                  rows={8}
                  placeholder="e.g. Brian Nunes · Wonderlust Charters · (555) 123-4567"
                  className="min-h-[10rem] w-full resize-y rounded-lg border border-bmail-border bg-bmail-surface px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold"
                />
              </div>
            );
          })}
          <p className="text-xs text-bmail-text-secondary">Footers are added to the bottom of emails you send from that mailbox.</p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Notifications</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <p className="mb-3 text-xs text-bmail-text-secondary">
            Get a push notification on this device when new mail arrives. Works in the installed app (iOS 16.4+).
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => {
                try {
                  const perm = await Notification.requestPermission();
                  if (perm !== 'granted') return alert('Notification permission was not granted.');
                  const reg = await navigator.serviceWorker.ready;
                  const { publicKey } = await fetch('/api/push/vapid-key', { credentials: 'include' }).then((r) => r.json());
                  // VAPID keys are base64URL (- and _ instead of + and /); atob only
                  // accepts standard base64, hence the explicit conversion first.
                  const b64 = publicKey.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(publicKey.length / 4) * 4, '=');
                  const sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
                  });
                  const j = sub.toJSON();
                  await pushSubscribe({ endpoint: j.endpoint!, keys: j.keys as { p256dh: string; auth: string } });
                  setPushOn(true);
                } catch (e: any) {
                  alert(`Couldn't enable notifications: ${e?.message || e}`);
                }
              }}
              className={pushOn ? 'hidden' : 'rounded-lg bg-bmail-goldfill px-3 py-1.5 text-sm font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover'}
            >
              Enable on this device
            </button>
            <button
              onClick={async () => {
                const reg = await navigator.serviceWorker.ready;
                const sub = await reg.pushManager.getSubscription();
                if (sub) { await pushUnsubscribe(sub.endpoint); await sub.unsubscribe(); }
                setPushOn(false);
              }}
              className={pushOn ? 'rounded-lg border border-bmail-border px-3 py-1.5 text-sm text-bmail-text hover:bg-bmail-surface-hover' : 'hidden'}
            >
              Disable on this device
            </button>
            <button
              onClick={() => pushTest().then(() => alert('Test sent — if enabled, a notification should appear now.'))}
              className="rounded-lg border border-bmail-border px-3 py-1.5 text-sm text-bmail-text hover:bg-bmail-surface-hover"
            >
              Send test
            </button>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">Default text</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <p className="mb-3 text-xs text-bmail-text-secondary">
            Applied to every email you send. Leave unset to use each recipient client's default.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-bmail-text">Font</label>
            <select
              value={defaultFont ?? ''}
              onChange={(e) => setDefaultFont(e.target.value || null)}
              className="rounded-lg border border-bmail-border bg-bmail-navy px-3 py-1.5 text-sm text-bmail-text"
            >
              <option value="">(no preference)</option>
              <option value="Arial">Arial</option>
              <option value="Verdana">Verdana</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Trebuchet MS">Trebuchet MS</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Courier New">Courier New</option>
            </select>
            <label className="text-sm text-bmail-text">Size</label>
            <select
              value={String(defaultFontSize ?? '')}
              onChange={(e) => setDefaultFontSize(e.target.value ? parseInt(e.target.value, 10) : null)}
              className="rounded-lg border border-bmail-border bg-bmail-navy px-3 py-1.5 text-sm text-bmail-text"
            >
              <option value="">(no preference)</option>
              {[12,13,14,15,16,18,20].map((n) => <option key={n} value={n}>{n}px</option>)}
            </select>
            <button
              disabled={saveDefaultText.isPending}
              onClick={() => saveDefaultText.mutate()}
              className="rounded-lg bg-bmail-goldfill px-3 py-1.5 text-sm font-medium text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50"
            >
              {saveDefaultText.isPending ? 'Saving…' : saveDefaultText.isSuccess ? 'Saved ✓' : 'Save'}
            </button>
          </div>
          <div
            className="mt-3 rounded-lg border border-bmail-border bg-bmail-navy p-3 text-sm"
            style={{
              fontFamily: defaultFont ? `'${defaultFont}', Arial, sans-serif` : undefined,
              fontSize: defaultFontSize ? `${defaultFontSize}px` : undefined
            }}
          >
            The quick brown fox jumps over the lazy dog — 1234567890
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-bmail-text-secondary">About</h2>
        <div className="rounded-xl border border-bmail-border bg-bmail-surface p-4">
          <div className="text-sm text-bmail-text">b-mail</div>
          <div className="text-xs text-bmail-text-secondary">
            v{__APP_VERSION__}
            {build?.commit ? ` · ${build.commit}` : ''}
            {build?.version && build.version !== __APP_VERSION__
              ? ` · server v${build.version} (reload to update)`
              : ''}
          </div>
        </div>
      </section>
    </div>
  );
}
